export const APP_NAME = import.meta.env.VITE_APP_NAME || 'Reybex Academy'

export const SUPPORTED_LOCALES = ['de', 'en', 'tr'] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]
export const DEFAULT_LOCALE: SupportedLocale = 'de'

/** Languages shown in pickers, in display order. Add `'tr'` to show Turkish again. */
export const SELECTABLE_LOCALES = ['de', 'en'] as const satisfies readonly SupportedLocale[]
export type SelectableLocale = (typeof SELECTABLE_LOCALES)[number]

export function isSelectableLocale(value: string | null | undefined): value is SelectableLocale {
  return !!value && (SELECTABLE_LOCALES as readonly string[]).includes(value)
}

export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  de: 'Deutsch',
  en: 'English',
  tr: 'Türkçe',
}

/** Playback position is persisted at most this often while a video plays. */
export const VIDEO_PROGRESS_THROTTLE_MS = 15_000

/** Default share of a video that must be watched before it auto-completes. */
export const DEFAULT_VIDEO_COMPLETE_PERCENT = 90

export const DEFAULT_PASSING_SCORE = 70
export const DEFAULT_COURSE_POINTS = 100

export const MAX_VIDEO_FILE_BYTES = 5 * 1024 * 1024 * 1024 // 5 GB
export const MAX_IMAGE_FILE_BYTES = 5 * 1024 * 1024 // 5 MB
export const MAX_RESOURCE_FILE_BYTES = 50 * 1024 * 1024 // 50 MB

/** Plain-text transcript attached to a video lesson (.txt, .vtt, .srt). */
export const MAX_TRANSCRIPT_FILE_BYTES = 1024 * 1024
export const MAX_TRANSCRIPT_CHARS = 200_000
export const ACCEPTED_TRANSCRIPT_EXTENSIONS = ['.txt', '.vtt', '.srt'] as const

export const ACCEPTED_VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/x-matroska',
] as const

/** Aliases used by the video-file Zod schema. */
export const MAX_VIDEO_SIZE_BYTES = MAX_VIDEO_FILE_BYTES
export const ACCEPTED_VIDEO_TYPES: readonly string[] = ACCEPTED_VIDEO_MIME_TYPES

export const ACCEPTED_IMAGE_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/avif',
] as const

export const STORAGE_BUCKETS = {
  /** Course thumbnails and cover images. Public read. Never video. */
  publicAssets: 'public-assets',
  /** Downloadable lesson attachments. Private, served via signed URLs. */
  resources: 'resources',
} as const

export const PAGE_SIZE = 12
export const ADMIN_PAGE_SIZE = 20
